import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { LoaderCircle, Paperclip } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { ulid } from 'ulid';

import { apiDeleteAsUser, apiGetAsUser, apiPostAsUser } from '../../lib/api_v2';
import { ImageRemixSession, createImageRemixApi } from '../../lib/imageRemix';
import { enqueueUpload, removeMediaAttachment, waitForMediaAttachmentUpload } from '../../lib/media_attachments_store';

import MediaAttachmentUploadPreview from '../MediaAttachmentUploadPreview';
import SettingsButton from '../shared/SettingsButton';
import SettingsTextarea from '../shared/SettingsTextarea';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';

const REFERENCE_IMAGE_TYPES = { 'image/jpeg': [], 'image/png': [], 'image/webp': [], 'image/gif': [], 'image/heic': [] };
const imageRemixApi = createImageRemixApi({ get: apiGetAsUser, post: apiPostAsUser, delete: apiDeleteAsUser });

const removeReferenceUpload = async (uploadId) => {
  try {
    await removeMediaAttachment(uploadId);
  } catch {
    // Unattached server uploads still expire through the pending-attachment purge.
  }
};

const attachmentImageUrl = (attachment) =>
  attachment?.public_url || attachment?.variants?.large?.public_url || attachment?.variants?.medium?.public_url || null;

const pauseForPoll = (signal) =>
  new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, 1000);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });

const submitRemixedMessage = (messageData) => {
  window.dispatchEvent(new CustomEvent('output:internal:live-journal:submit-message', { detail: { messageData } }));
};

/** Edits one unpublished image and hands its final result to the normal chat sender. */
export const ImageRemixModal = ({
  sourceAttachment,
  channelId,
  workspaceId,
  active = true,
  sessionKey,
  onClose,
  onBusyChange,
  api = imageRemixApi,
  submitMessage = submitRemixedMessage,
}) => {
  const [session] = useState(
    () =>
      new ImageRemixSession({
        api,
        channelId,
        workspaceId,
        sourceAttachmentId: sourceAttachment.id,
        uuid: () => crypto.randomUUID(),
        messageId: ulid,
        submit: submitMessage,
        pause: pauseForPoll,
      }),
  );
  const view = useSyncExternalStore(session.subscribe, session.snapshot);
  const [prompt, setPrompt] = useState('');
  const [referenceImages, setReferenceImages] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadingRef = useRef(false);
  const activeRef = useRef(active);
  const uploadIdsRef = useRef(new Set());
  const isGenerating = view.phase === 'previewing';
  const isBusy = isUploading || isGenerating || view.phase === 'sending';
  const previewAttachment = view.attachment;
  const imageUrl = attachmentImageUrl(previewAttachment || sourceAttachment);
  const error = uploadError || view.error;

  useEffect(() => {
    activeRef.current = active;
    if (!active) session.close();
  }, [active, session]);

  useEffect(
    () => () => {
      activeRef.current = false;
      session.close();
      for (const uploadId of uploadIdsRef.current) void removeReferenceUpload(uploadId);
    },
    [session],
  );

  const handleReferenceFiles = useCallback(
    async (files) => {
      if (!activeRef.current || uploadingRef.current || session.snapshot().phase !== 'editing') return;
      if (referenceImages.length + files.length > 2) {
        setUploadError('You can add up to two reference images.');
        return;
      }
      uploadingRef.current = true;
      setIsUploading(true);
      setUploadError(null);
      onBusyChange?.(true, sessionKey);
      let pendingUploadId = null;
      try {
        for (const file of files) {
          const uploadId = await enqueueUpload({
            file,
            purpose: 'message_attachments',
            workspaceId,
            sessionKey: `image-remix:${sessionKey}`,
          });
          pendingUploadId = uploadId;
          uploadIdsRef.current.add(uploadId);
          if (!activeRef.current) {
            await removeReferenceUpload(uploadId);
            break;
          }
          const attachment = await waitForMediaAttachmentUpload(uploadId);
          if (!activeRef.current) break;
          setReferenceImages((images) => [...images, attachment]);
          pendingUploadId = null;
        }
      } catch (error) {
        if (pendingUploadId) {
          uploadIdsRef.current.delete(pendingUploadId);
          await removeReferenceUpload(pendingUploadId);
        }
        if (activeRef.current) setUploadError(`Could not attach the reference image: ${error.message || 'upload failed'}.`);
      } finally {
        uploadingRef.current = false;
        if (activeRef.current) setIsUploading(false);
        onBusyChange?.(false, sessionKey);
      }
    },
    [onBusyChange, referenceImages.length, session, sessionKey, workspaceId],
  );

  const handleReferenceRejected = useCallback(() => {
    setUploadError('Choose up to two JPEG, PNG, WebP, GIF, or HEIC images, each under 25 MB.');
  }, []);
  const { getInputProps, open: openReferencePicker } = useDropzone({
    accept: REFERENCE_IMAGE_TYPES,
    maxSize: 25 * 1024 * 1024,
    maxFiles: 2,
    noClick: true,
    noKeyboard: true,
    disabled: !active || isBusy || view.phase !== 'editing' || referenceImages.length >= 2,
    onDropAccepted: handleReferenceFiles,
    onDropRejected: handleReferenceRejected,
  });
  const handleRemoveReference = useCallback(
    (attachment) => {
      if (uploadingRef.current || session.snapshot().phase !== 'editing') return;
      setReferenceImages((images) => images.filter((image) => image.id !== attachment.id));
      uploadIdsRef.current.delete(attachment.id);
      void removeReferenceUpload(attachment.id);
      setUploadError(null);
    },
    [session],
  );

  const handlePromptChange = useCallback((event) => setPrompt(event.target.value), []);
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!active || uploadingRef.current || session.snapshot().phase !== 'editing' || !prompt.trim()) return;
      onBusyChange?.(true, sessionKey);
      setUploadError(null);
      void session
        .preview(
          prompt,
          referenceImages.map((image) => image.attachment_id),
        )
        .finally(() => onBusyChange?.(false, sessionKey));
    },
    [active, onBusyChange, prompt, referenceImages, session, sessionKey],
  );
  const handleClose = useCallback(() => {
    const phase = session.snapshot().phase;
    if (uploadingRef.current || phase === 'previewing' || phase === 'sending') return;
    session.close();
    onClose();
  }, [onClose, session]);
  const handleSend = useCallback(async () => {
    const current = session.snapshot();
    if (!active || uploadingRef.current || !current.attachment || !['editing', 'handoff-uncertain'].includes(current.phase)) return;
    onBusyChange?.(true, sessionKey);
    const sent = await session.send();
    onBusyChange?.(false, sessionKey);
    if (sent) onClose();
  }, [active, onClose, onBusyChange, session, sessionKey]);

  return (
    <Modal onClose={handleClose} showClose={!isBusy} padding="none" className="bg-gray-100 dark:bg-neutral-950">
      <ModalBody padding="none" className="min-h-0">
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" aria-label="Remix image">
          <div className="relative min-h-32 flex-1 overflow-hidden bg-black/90">
            {imageUrl ? <img src={imageUrl} alt="Image to remix" className="absolute inset-0 size-full object-contain p-4" /> : null}
            {isBusy ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                <LoaderCircle
                  className="size-8 animate-spin text-white"
                  role="status"
                  aria-label={isUploading ? 'Uploading reference image' : isGenerating ? 'Generating preview' : 'Sending image'}
                />
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col gap-3 p-4 md:p-6">
            <SettingsTextarea
              autoFocus
              value={prompt}
              onChange={handlePromptChange}
              rows={4}
              maxLength={10000}
              disabled={isBusy || !active}
              placeholder="Describe how you want to change this image"
              aria-label="Remix prompt"
              className="h-auto min-h-28"
            />
            <input
              {...getInputProps()}
              aria-label="Add reference images"
              disabled={!active || isBusy || view.phase !== 'editing' || referenceImages.length >= 2}
            />
            <div className="flex flex-wrap items-center gap-2">
              {referenceImages.map((attachment) => (
                <MediaAttachmentUploadPreview
                  key={attachment.id}
                  mediaAttachment={attachment}
                  size="size-14"
                  showRemoveButton={!isBusy && view.phase === 'editing'}
                  onRemove={handleRemoveReference}
                />
              ))}
              <SettingsButton
                type="button"
                variant="secondary"
                onClick={openReferencePicker}
                className="inline-flex items-center gap-2"
                disabled={!active || isBusy || view.phase !== 'editing' || referenceImages.length >= 2}
              >
                <Paperclip className="size-4" /> Add reference image
              </SettingsButton>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <SettingsButton type="button" variant="secondary" onClick={handleClose} disabled={isBusy || !active}>
                Cancel
              </SettingsButton>
              <SettingsButton type="submit" disabled={!active || isBusy || view.phase !== 'editing' || !prompt.trim()}>
                Preview
              </SettingsButton>
              {previewAttachment ? (
                <SettingsButton type="button" onClick={handleSend} disabled={isBusy || !active}>
                  Send
                </SettingsButton>
              ) : null}
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ImageRemixModal;
